(() => {
  'use strict';

  const SELECTORS = {
    toggle: '[data-sg-filter-toggle]',
    remove: '.t4s-active-filters__remove-filter',
    clear: '[data-sg-clear-all]',
    resultCount: '[data-sg-results-count]',
    status: '[data-sg-facet-status]',
    filterButton: '.t4s-btn-filter',
    drawer: '#t4s-filter-hidden.is--t4s-filter-native, #t4s-filter-hidden[data-filter-links]',
    filterRoot: '[data-filter-links]',
    ajaxContainer: '[data-ntajax-container]'
  };

  let pendingAction = null;
  let filterOpener = null;
  let fallbackTimer = null;
  const drawerStates = new WeakMap();

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const isVisible = (element) => {
    if (!element) return false;
    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  };

  const getStatus = () => {
    let status = document.querySelector(SELECTORS.status);
    if (!status) {
      status = document.createElement('div');
      status.className = 'sg-visually-hidden';
      status.dataset.sgFacetStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      document.body.appendChild(status);
    }
    return status;
  };

  const announce = (message) => {
    const status = getStatus();
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const getResultCount = () => {
    const counters = Array.from(document.querySelectorAll(SELECTORS.resultCount));
    const counter = counters.find(isVisible) || counters[0];
    if (!counter) return null;
    const count = Number.parseInt(counter.getAttribute('data-sg-results-count'), 10);
    return Number.isFinite(count) ? count : null;
  };

  const resultText = (count) => {
    if (count === null) return 'Results updated.';
    return `${count} ${count === 1 ? 'product' : 'products'} shown.`;
  };

  const getDrawer = () => document.querySelector(SELECTORS.drawer);

  const drawerIsOpen = (drawer) => {
    if (!drawer) return false;
    return drawer.getAttribute('aria-hidden') === 'false' || drawer.classList.contains('is--opened') || drawer.classList.contains('t4s-drawer-open');
  };

  const findVisible = (selector) => {
    const elements = Array.from(document.querySelectorAll(selector));
    return elements.find(isVisible) || elements[0] || null;
  };

  const focusElement = (element) => {
    if (!element || typeof element.focus !== 'function') return;
    window.setTimeout(() => {
      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
    }, 40);
  };

  const enhance = (root = document) => {
    root.querySelectorAll(SELECTORS.toggle).forEach((toggle) => {
      toggle.setAttribute('role', toggle.dataset.sgSingleSelect === 'true' ? 'radio' : 'checkbox');
      if (!toggle.hasAttribute('aria-checked')) {
        toggle.setAttribute('aria-checked', toggle.closest('li')?.classList.contains('is--selected') ? 'true' : 'false');
      }
      if (toggle.closest('li')?.classList.contains('is--disabled')) {
        toggle.setAttribute('aria-disabled', 'true');
        toggle.setAttribute('tabindex', '-1');
        toggle.removeAttribute('href');
      }
    });

    root.querySelectorAll(SELECTORS.remove).forEach((remove) => {
      if (!remove.hasAttribute('aria-label')) {
        remove.setAttribute('aria-label', `Remove ${remove.textContent.trim()} filter`);
      }
    });

    root.querySelectorAll(SELECTORS.clear).forEach((clear) => {
      if (!clear.hasAttribute('aria-label')) clear.setAttribute('aria-label', 'Clear all filters');
    });

    root.querySelectorAll(SELECTORS.filterButton).forEach((button) => {
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', 't4s-filter-hidden');
      if (!button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
    });

    const drawer = getDrawer();
    if (drawer) {
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      if (!drawer.hasAttribute('aria-label')) drawer.setAttribute('aria-label', 'Filter products');
      if (!drawerStates.has(drawer)) drawerStates.set(drawer, drawerIsOpen(drawer));
    }
  };

  const restoreFocus = (action) => {
    const drawer = getDrawer();
    const open = drawerIsOpen(drawer);

    if (action.type === 'sort') {
      const sortControl = findVisible('.t4s-dropdown__sortby > button, [data-filter-sortby] button, [data-filter-sortby]');
      focusElement(sortControl);
      return;
    }

    if (action.type === 'clear' || action.type === 'history') {
      focusElement(filterOpener || findVisible(SELECTORS.filterButton));
      return;
    }

    if (action.key && !action.fromDrawer && open === false) {
      const selector = `${SELECTORS.toggle}[data-sg-filter-key="${escapeSelector(action.key)}"]`;
      const toggle = findVisible(selector);
      if (toggle) {
        focusElement(toggle);
        return;
      }
    }

    if (action.key && !action.fromDrawer) {
      const selector = `${SELECTORS.toggle}[data-sg-filter-key="${escapeSelector(action.key)}"]`;
      const toggle = findVisible(selector);
      if (toggle) {
        focusElement(toggle);
        return;
      }
    }

    focusElement(filterOpener || findVisible(SELECTORS.filterButton));
  };

  const finaliseAction = () => {
    if (!pendingAction) return;
    window.clearTimeout(fallbackTimer);

    const action = pendingAction;
    pendingAction = null;
    const count = getResultCount();
    const suffix = resultText(count);

    switch (action.type) {
      case 'filter':
        announce(`${action.label} ${action.selecting ? 'selected' : 'removed'}. ${suffix}`);
        break;
      case 'remove':
        announce(`${action.label} removed. ${suffix}`);
        break;
      case 'clear':
        announce(`All filters cleared. ${suffix}`);
        break;
      case 'sort':
        announce(`Sort order updated. ${suffix}`);
        break;
      case 'history':
        announce(`Filters restored. ${suffix}`);
        break;
      default:
        announce(suffix);
    }

    restoreFocus(action);
  };

  const scheduleFallback = (delay = 1800) => {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = window.setTimeout(finaliseAction, delay);
  };

  document.addEventListener('click', (event) => {
    const filterButton = event.target.closest(SELECTORS.filterButton);
    if (filterButton) {
      filterOpener = filterButton;
      filterButton.setAttribute('aria-expanded', 'true');
      return;
    }

    const closeButton = event.target.closest('#t4s-filter-hidden [data-drawer-close]');
    if (closeButton) {
      document.querySelectorAll(SELECTORS.filterButton).forEach((button) => button.setAttribute('aria-expanded', 'false'));
      focusElement(filterOpener || findVisible(SELECTORS.filterButton));
      return;
    }

    const disabledToggle = event.target.closest(`${SELECTORS.toggle}[aria-disabled="true"]`);
    if (disabledToggle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      announce(`${disabledToggle.dataset.sgFilterLabel || 'This filter'} is unavailable.`);
      return;
    }

    const toggle = event.target.closest(SELECTORS.toggle);
    if (toggle) {
      pendingAction = {
        type: 'filter',
        key: toggle.dataset.sgFilterKey || '',
        label: toggle.dataset.sgFilterLabel || toggle.textContent.trim(),
        selecting: toggle.getAttribute('aria-checked') !== 'true',
        fromDrawer: Boolean(toggle.closest('.t4s-drawer'))
      };
      scheduleFallback();
      return;
    }

    const remove = event.target.closest(SELECTORS.remove);
    if (remove) {
      pendingAction = {
        type: 'remove',
        key: remove.dataset.sgFilterKey || '',
        label: remove.dataset.sgFilterLabel || remove.textContent.replace('×', '').trim(),
        fromDrawer: false
      };
      scheduleFallback();
      return;
    }

    const clear = event.target.closest(SELECTORS.clear);
    if (clear) {
      pendingAction = { type: 'clear', fromDrawer: Boolean(clear.closest('.t4s-drawer')) };
      scheduleFallback();
      return;
    }

    const sort = event.target.closest('[data-sortby-item], [data-filter-sortby] a');
    if (sort) {
      pendingAction = { type: 'sort' };
      scheduleFallback(500);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const toggle = event.target.closest(SELECTORS.toggle);
    if (!toggle || (event.key !== ' ' && event.key !== 'Spacebar')) return;

    event.preventDefault();
    if (toggle.getAttribute('aria-disabled') === 'true') {
      announce(`${toggle.dataset.sgFilterLabel || 'This filter'} is unavailable.`);
      return;
    }
    toggle.click();
  });

  window.addEventListener('popstate', () => {
    pendingAction = { type: 'history' };
    scheduleFallback();
  }, true);

  // Batch updates while preserving a full scan whenever filter controls change.
  let enhancementQueued = false;
  let enhancementNeedsFullDocument = false;
  const observer = new MutationObserver((mutations) => {
    let mainUpdated = false;
    let filterUpdated = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        const drawer = mutation.target.closest?.(SELECTORS.drawer) || (mutation.target.matches?.(SELECTORS.drawer) ? mutation.target : null);
        if (drawer) {
          const isOpen = drawerIsOpen(drawer);
          const wasOpen = drawerStates.get(drawer);
          drawerStates.set(drawer, isOpen);

          document.querySelectorAll(SELECTORS.filterButton).forEach((button) => {
            button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          });

          if (wasOpen === false && isOpen === true) {
            window.setTimeout(() => {
              if (!drawer.contains(document.activeElement)) {
                focusElement(drawer.querySelector('[data-drawer-close]') || drawer.querySelector(SELECTORS.toggle));
              }
            }, 80);
          }

          if (wasOpen === true && isOpen === false) {
            focusElement(filterOpener || findVisible(SELECTORS.filterButton));
          }
        }
      }

      if (mutation.type !== 'childList') return;
      const target = mutation.target;
      if (target.matches?.(SELECTORS.ajaxContainer) || target.closest?.(SELECTORS.ajaxContainer)) mainUpdated = true;
      if (target.matches?.(SELECTORS.filterRoot) || target.closest?.(SELECTORS.filterRoot)) filterUpdated = true;
    });

    if (mainUpdated || filterUpdated) {
      if (filterUpdated) enhancementNeedsFullDocument = true;
      if (!enhancementQueued) {
        enhancementQueued = true;
        window.requestAnimationFrame(() => {
          enhancementQueued = false;
          const root = enhancementNeedsFullDocument
            ? document
            : (document.querySelector(SELECTORS.ajaxContainer) || document);
          enhancementNeedsFullDocument = false;
          enhance(root);
        });
      }
    }
    if (!pendingAction) return;

    if (filterUpdated) {
      window.setTimeout(finaliseAction, 60);
    } else if (mainUpdated && pendingAction.type === 'sort') {
      window.setTimeout(finaliseAction, 100);
    } else if (mainUpdated) {
      scheduleFallback(1500);
    }
  });

  const start = () => {
    enhance(document);
    getStatus();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
